import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Building2, Globe } from 'lucide-react'
import { PageTemplate } from '@/components/templates'
import { CONTAINER_SIZES } from '@/constants'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { tradingService } from '@/services/tradingService'
import { OrderDetails } from '@/types'
import { formatCurrency, formatDate } from '@/utils'
import { PlaceBidModal } from '@/components/organisms/modals'

const OrderDetailsScreen = () => {
  const { orderId } = useParams()
  const navigate = useNavigate()
  const [order, setOrder] = useState<OrderDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [placeBidModal, setPlaceBidModal] = useState(false)

  useEffect(() => {
    loadOrder()
  }, [orderId])

  const loadOrder = async () => {
    if (!orderId) return
    try {
      setLoading(true)
      const response = await tradingService.getOrderDetails(orderId)
      setOrder(response)
    } catch (error) {
      console.error('Error loading order:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleBidPlaced = () => {
    setPlaceBidModal(false)
    loadOrder()
  }

  if (loading) {
    return <PageTemplate isLoading />
  }

  if (!order) {
    return (
      <PageTemplate maxWidth={CONTAINER_SIZES.WIDE}>
        <div className="text-center py-8">
          <h2 className="text-xl font-semibold mb-2">Order not found</h2>
          <Button onClick={() => navigate('/trading/otc')}>
            Back to Orders
          </Button>
        </div>
      </PageTemplate>
    )
  }

  return (
    <PageTemplate maxWidth={CONTAINER_SIZES.WIDE}>
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center">
              <Button
                variant="ghost"
                onClick={() => navigate('/trading/otc')}
                className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <ArrowLeft size={20} />
                <span className="font-medium">Back to Orders</span>
              </Button>
            </div>
            <div className="text-center">
              <h1 className="text-2xl font-bold text-[var(--text)]">Order Details</h1>
            </div>
            <div className="w-32"></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Order Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Order Details Card */}
            <Card variant="elevated" className="lg:col-span-2">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <Building2 size={20} />
                  Order Details
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Type</span>
                      <Badge variant={order.type === 'SELL' ? 'destructive' : 'default'}>
                        {order.type}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Asset Type</span>
                      <span className="text-sm font-semibold text-[var(--text)]">{order.deal_type}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Round</span>
                      <span className="text-sm font-semibold text-[var(--text)]">{order.round || '—'}</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Amount</span>
                      <span className="text-sm font-semibold text-[var(--text)]">{formatCurrency(order.offered_amount)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">FDV</span>
                      <span className="text-sm font-semibold text-[var(--text)]">{formatCurrency(order.offered_fully_diluted_value)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Date</span>
                      <span className="text-sm font-semibold text-[var(--text)]">{formatDate(order.date)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Project Information Card */}
            <Card variant="elevated">
              <CardHeader className="pb-4">
                <CardTitle>Project Information</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-4">
                  {order.description && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Description</h3>
                      <p className="text-sm text-[var(--text)] leading-relaxed">{order.description}</p>
                    </div>
                  )}
                  {order.website && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Website</h3>
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="w-full justify-start"
                      >
                        <a
                          href={order.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2"
                        >
                          <Globe size={16} />
                          Visit Website
                        </a>
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end">
            <Button
              onClick={() => setPlaceBidModal(true)}
              className="px-6"
            >
              Place Bid
            </Button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {placeBidModal && order && (
        <PlaceBidModal
          isOpen={placeBidModal}
          order={order}
          onClose={() => setPlaceBidModal(false)}
          onBidPlaced={handleBidPlaced}
        />
      )}
    </PageTemplate>
  )
}

export default OrderDetailsScreen 